import { createSignal, For, Show } from "solid-js";
import { Portal } from "solid-js/web";
import Text from "./Text";
import { useTranslation } from "../hooks/useTranslation";
import { useAnchoredMenu } from "../hooks/useAnchoredMenu";
import menuStyles from "./StorageSelect.module.css";
import styles from "./PlaceholderChip.module.css";

interface PlaceholderFormat {
    labelKey: string;
    example: string;
    template: (key: string) => string;
}

// Single quotes, so an inserted call never ends the JSON string of a raw webhook body.
const NUMBER_FORMATS: PlaceholderFormat[] = [
    { labelKey: "AUTOMATION_FORMAT_AS_IS", example: "30000000", template: key => `{${key}}` },
    { labelKey: "AUTOMATION_FORMAT_SHORT", example: "30m", template: key => `{abbreviate(${key}, 'k|m|b')}` },
    { labelKey: "AUTOMATION_FORMAT_SHORT_UPPER", example: "30M", template: key => `{abbreviate(${key}, 'K|M|B')}` },
    { labelKey: "AUTOMATION_FORMAT_WORDS", example: "30 Mio.", template: key => `{abbreviate(${key}, ' Tsd.| Mio.| Mrd.')}` },
    { labelKey: "AUTOMATION_FORMAT_CURRENCY_WHOLE", example: "30.000.000 $", template: key => `{currency(${key}, '$', 0)}` },
    { labelKey: "AUTOMATION_FORMAT_CURRENCY", example: "30.000.000,00 $", template: key => `{currency(${key}, '$')}` },
];

const DATETIME_FORMATS: PlaceholderFormat[] = [
    { labelKey: "AUTOMATION_FORMAT_DATE_TIME", example: "02.11.2026, 14:30", template: key => `{date(${key})}` },
    { labelKey: "AUTOMATION_FORMAT_DATE", example: "02.11.2026", template: key => `{date(${key}, 'date')}` },
    { labelKey: "AUTOMATION_FORMAT_TIME", example: "14:30", template: key => `{date(${key}, 'time')}` },
    { labelKey: "AUTOMATION_FORMAT_DATE_LONG", example: "02.11.2026, 14:30:05", template: key => `{date(${key}, 'long')}` },
    { labelKey: "AUTOMATION_FORMAT_AGO", example: "5m", template: key => `{ago(${key})}` },
];

const TEXT_FORMATS: PlaceholderFormat[] = [
    { labelKey: "AUTOMATION_FORMAT_AS_IS", example: "§aNotch", template: key => `{${key}}` },
    { labelKey: "AUTOMATION_FORMAT_STRIP_COLORS", example: "Notch", template: key => `{stripcolors(${key})}` },
    { labelKey: "AUTOMATION_FORMAT_UPPER", example: "NOTCH", template: key => `{upper(${key})}` },
    { labelKey: "AUTOMATION_FORMAT_LOWER", example: "notch", template: key => `{lower(${key})}` },
];

const FORMATS_BY_TYPE: Record<string, PlaceholderFormat[]> = {
    number: NUMBER_FORMATS,
    datetime: DATETIME_FORMATS,
    string: TEXT_FORMATS,
};

// The template language has no escapes, so these would end the quoted argument or the placeholder.
const suffixSafe = (suffix: string) => suffix.replace(/['|{}]/g, "");

interface Props {
    paramKey: string;
    type: string;
    onInsert: (template: string) => void;
}

export default function PlaceholderChip(props: Props) {
    const { t } = useTranslation();
    const menu = useAnchoredMenu(420);
    const [thousand, setThousand] = createSignal("k");
    const [million, setMillion] = createSignal("m");
    const [billion, setBillion] = createSignal("b");

    const formats = () => FORMATS_BY_TYPE[props.type] ?? [];

    const insert = (template: string) => {
        props.onInsert(template);
        menu.setOpen(false);
    };

    const onChipClick = () => {
        if (formats().length === 0) {
            props.onInsert(`{${props.paramKey}}`);
            return;
        }
        menu.toggle();
    };

    const customTemplate = () => `{abbreviate(${props.paramKey}, '${thousand()}|${million()}|${billion()}')}`;

    const suffixInput = (labelKey: string, value: () => string, setValue: (next: string) => void) => (
        <label class={styles.suffixField}>
            <span><Text val={labelKey} /></span>
            <input type="text" value={value()} maxLength={8} onInput={(e) => setValue(suffixSafe(e.currentTarget.value))} />
        </label>
    );

    return (
        <span ref={menu.setContainerRef}>
            <button
                type="button"
                ref={menu.setTriggerRef}
                class={styles.chip}
                classList={{ [styles.chipOpen]: menu.open() }}
                aria-haspopup={formats().length > 0 ? "menu" : undefined}
                aria-expanded={formats().length > 0 ? menu.open() : undefined}
                onClick={onChipClick}
            >
                {`{${props.paramKey}}`}
                <Show when={formats().length > 0}>
                    <i class={`fa-solid fa-chevron-down ${styles.chipChevron}`} aria-hidden="true" />
                </Show>
            </button>

            <Show when={menu.open()}>
                <Portal mount={document.body}>
                    <div
                        ref={menu.setMenuRef}
                        class={menuStyles.dropdown}
                        role="menu"
                        style={{
                            position: "fixed",
                            left: `min(${menu.menuRect().left}px, calc(100vw - 20rem - var(--spacing-lg)))`,
                            top: `${menu.menuRect().top}px`,
                        }}
                        onKeyDown={(e) => { if (e.key === "Escape") menu.setOpen(false); }}
                    >
                        <div class={menuStyles.list}>
                            <div class={`${menuStyles.sectionHeader} ${styles.menuTitle}`}>{t("AUTOMATION_FORMAT_TITLE", `{${props.paramKey}}`)}</div>
                            <For each={formats()}>
                                {(format) => (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        class={`${menuStyles.option} ${styles.formatOption}`}
                                        onClick={() => insert(format.template(props.paramKey))}
                                    >
                                        <span class={styles.formatLabel}><Text val={format.labelKey} /></span>
                                        <span class={styles.formatExample}>{format.example}</span>
                                    </button>
                                )}
                            </For>

                            <Show when={props.type === "number"}>
                                <div class={menuStyles.sectionHeader}><Text val="AUTOMATION_FORMAT_CUSTOM" /></div>
                                <div class={styles.custom}>
                                    <div class={styles.suffixRow}>
                                        {suffixInput("AUTOMATION_FORMAT_THOUSAND", thousand, setThousand)}
                                        {suffixInput("AUTOMATION_FORMAT_MILLION", million, setMillion)}
                                        {suffixInput("AUTOMATION_FORMAT_BILLION", billion, setBillion)}
                                    </div>
                                    <span class={styles.customHint}><Text val="AUTOMATION_FORMAT_CUSTOM_HINT" /></span>
                                    <div class={styles.customFooter}>
                                        <span class={styles.formatExample}>
                                            {`${thousand() ? `1,5${thousand()}` : "1.500"} / 30${million() || ".000.000"}`}
                                        </span>
                                        <button type="button" class="btn btn-secondary btn-sm" onClick={() => insert(customTemplate())}>
                                            <i class="fa-solid fa-plus" /> <Text val="AUTOMATION_FORMAT_INSERT" />
                                        </button>
                                    </div>
                                </div>
                            </Show>
                        </div>
                    </div>
                </Portal>
            </Show>
        </span>
    );
}
